import { FormQuestion } from "domain/entities/form-question";
import { FormQuestionOptions } from "domain/entities/form-question-options";
import { QuestionTypes } from "domain/enum/questionTypes";

import { useFormsEditStore } from "presentation/stores/use-forms-edit.store";

import { useEffect, useRef, useState } from "react";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable, DataTableValueArray } from "primereact/datatable";

import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

type ManageQuestionProps = {
  questionId: string | null;
  onClose: () => void;
};

const ManageQuestion = ({ questionId, onClose }: ManageQuestionProps) => {
  const state = useFormsEditStore();

  const dt = useRef<DataTable<DataTableValueArray>>(null);

  const [type, setType] = useState<QuestionTypes>(QuestionTypes.FREE_TEXT);

  const [label, setLabel] = useState<string>("");

  const [isRequired, setIsRequired] = useState<boolean>(true);

  const [newQuestionId, setNewQuestionId] = useState<string | null>(null);

  const [newOptionLabel, setNewOptionLabel] = useState<string>("");

  const [newOptions, setNewOptions] = useState<FormQuestionOptions[]>([]);

  const types = Object.values(QuestionTypes).map((value) => ({
    label: value,
    value,
  }));

  const parseQuestionType = (value: string): QuestionTypes | undefined => {
    return Object.values(QuestionTypes).find((v) => v === value);
  };

  const removeOption = (optionId: string) => {
    if (questionId === null) {
      setNewOptions((prev) => prev.filter((option) => option.id !== optionId));

      return;
    }

    const updatedState = useFormsEditStore.getState();
    state.setFormsEdit({
      questionOptions: updatedState.formData.questionOptions.filter(
        (option) => option.id !== optionId,
      ),
    });
  };

  const addNewOption = () => {
    if (questionId === null) {
      if (newOptionLabel.trim() === "" || newQuestionId === null) {
        return;
      }

      const option: FormQuestionOptions = {
        id: crypto.randomUUID(),
        value: newOptionLabel,
        questionId: newQuestionId,
        createdAt: new Date(),
        modifiedAt: null,
      };

      setNewOptions((prev) => [...prev, option]);

      setNewOptionLabel("");

      return;
    }

    if (newOptionLabel.trim() === "") {
      return;
    }

    const option: FormQuestionOptions = {
      id: crypto.randomUUID(),
      value: newOptionLabel,
      questionId: questionId,
      createdAt: new Date(),
      modifiedAt: null,
    };

    state.setFormsEdit({
      questionOptions: [...state.formData.questionOptions, option],
    });

    setNewOptionLabel("");
  };

  const updateQuestionType = (questionType: QuestionTypes) => {
    if (questionId === null) {
      return;
    }

    state.setFormsEdit({
      questions: state.formData.questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              type: questionType,
            }
          : question,
      ),
    });
  };

  const updateQuestionLabel = (value: string) => {
    if (questionId === null) {
      return;
    }

    state.setFormsEdit({
      questions: state.formData.questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              label: value,
            }
          : question,
      ),
    });
  };

  const updateQuestionRequired = (required: boolean) => {
    if (questionId === null) {
      return;
    }

    state.setFormsEdit({
      questions: state.formData.questions.map((question) =>
        question.id === questionId
          ? {
              ...question,
              required,
            }
          : question,
      ),
    });
  };

  const addQuestion = (): boolean => {
    if (newQuestionId === null || label.trim() === "") {
      return false;
    }

    const newQuestion: FormQuestion = {
      id: newQuestionId,
      label,
      order: state.formData.totalQuestions + 1,
      required: isRequired,
      templateId: state.formData.formTemplate.id,
      type,
      active: true,
      questionOptions: newOptions,
      createdAt: new Date(),
      modifiedAt: null,
    };

    state.setFormsEdit({
      questions: [...state.formData.questions, newQuestion],

      questionOptions: [...state.formData.questionOptions, ...newOptions],

      addedQuestions: [...state.formData.addedQuestions, newQuestionId],

      totalQuestions: state.formData.totalQuestions + 1,
    });

    return true;
  };

  useEffect(() => {
    if (questionId === null) {
      return;
    }

    const question = state.formData.questions.find(
      (question) => question.id === questionId,
    );

    const questionType = parseQuestionType(question?.type ?? "");

    setType(questionType ?? QuestionTypes.FREE_TEXT);

    setLabel(question?.label ?? "");

    setIsRequired(question?.required ?? true);
  }, [questionId, state.formData.questions]);

  useEffect(() => {
    if (questionId === null) {
      setNewQuestionId(crypto.randomUUID());
    }
  }, [questionId]);

  const currentOptions =
    questionId === null && newQuestionId !== null
      ? newOptions.filter((option) => option.questionId === newQuestionId)
      : state.formData.questionOptions.filter(
          (option) => option.questionId === questionId,
        );

  return (
    <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        height: "100%",
        justifyContent: "space-between"
    }}>
      <div style={{
            paddingLeft: "1.5rem",
            paddingRight: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
        }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            <label
              htmlFor="label"
              style={{
                fontWeight: 600,
                fontSize: "0.95rem",
              }}
            >
              Nome
            </label>
            <InputText
              id="label"
              value={label}
              placeholder="Ex: Qual o seu nível de satisfação?"
              onChange={(e) => {
                setLabel(e.target.value);
                updateQuestionLabel(e.target.value);
              }}
              style={{
                width: "100%",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "end",
              gap: "1rem",
              width: "100%",
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <label
                htmlFor="type"
                style={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                }}
              >
                Tipo da Pergunta
              </label>
              <Dropdown
                id="type"
                value={type}
                options={types}
                onChange={(e) => {
                  setType(e.value);
                  updateQuestionType(e.value);
                }}
                placeholder="Selecione..."
                style={{
                  width: "100%",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                paddingBottom: "0.75rem",
                whiteSpace: "nowrap",
              }}
            >
              <Checkbox
                inputId="required"
                checked={isRequired}
                onChange={(e) => {
                  setIsRequired(e.checked ?? false);
                  updateQuestionRequired(e.checked ?? false);
                }}
              />
              <label
                htmlFor="required"
                style={{
                  fontWeight: 500,
                }}
              >
                Obrigatória
              </label>
            </div>
          </div>
          {type === QuestionTypes.MULTIPLE && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                <label
                  style={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  Nova opção
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                  }}
                >
                  <InputText
                    value={newOptionLabel}
                    onChange={(e) => setNewOptionLabel(e.target.value)}
                    placeholder="Digite o texto da opção..."
                    style={{
                      flex: 1,
                    }}
                  />
                  <Button
                    type="button"
                    icon="pi pi-plus"
                    onClick={() => addNewOption()}
                  />
                </div>
              </div>
              <DataTable
                ref={dt}
                value={currentOptions}
                dataKey="id"
                emptyMessage="Nenhuma opção disponível"
                size="small"
                scrollable
                scrollHeight={currentOptions.length > 3 ? "220px" : undefined}
              >
                <Column
                  field="value"
                  header="VALOR"
                  style={{
                    width: "100%",
                  }}
                />
                <Column
                  header="AÇÕES"
                  style={{
                    width: "6rem",
                    textAlign: "center",
                  }}
                  body={(rowData: FormQuestionOptions) => (
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      severity="danger"
                      text
                      onClick={() => removeOption(rowData.id)}
                    />
                  )}
                />
              </DataTable>
            </div>
          )}
      </div>
      {questionId === null && (
        <div style={{
            paddingLeft: "1.5rem",
            paddingRight: "1.5rem",
            display: "flex",
            flexDirection: "column",
            paddingTop: "1rem",
        }}>
          <Button
            type="button"
            label="Adicionar"
            style={{
              width: "100%",
              justifyContent: "flex-end", 
            }}
            onClick={() => {
              if (addQuestion()) {
                onClose();
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ManageQuestion;
